import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import { AdditionalProps } from 'react-onclickoutside';
import cn from 'classnames';

import { Group, Item, Option } from '../types';
import { useTheme } from '../../theme';
import Dropdown from '../../Dropdown/Dropdown';
import Input from '../../Input';

export interface DropdownRendererParameters<T> {
	options: Item<T>[];
	isOpen: boolean;
	onClose: () => void;
	onSelect: (value: T) => void;
	onAdditionalButtonClick?: () => void;
	onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	renderOption?: (option: Option<T>, key: number) => React.ReactNode;
	renderGroup?: (group: Group<T>, key: number) => React.ReactNode;
}

export interface OwnProps<T> {
	className?: string;

	/**
	 * Class name for the dropdown block.
	 */
	dropDownClassName?: string;

	/**
	 * Loading is in progess.
	 */
	isLoading?: boolean;

	/**
	 * List of possible options.
	 */
	options: Item<T>[];

	/**
	 * Input value for the text field.
	 */
	inputValue?: string;

	/**
	 * Function for rendering custom input component.
	 */
	inputRenderer?: (
		isOpen: boolean,
		isLoading: boolean,
		inputProps: React.InputHTMLAttributes<HTMLInputElement>
	) => React.ReactNode;

	/**
	 * Function for rendering custom options group block.
	 */
	groupRenderer?: (
		group: Group<T>,
		key: number,
		optionRenderer: (option: Option<T>, key: number) => React.ReactNode
	) => React.ReactNode;

	/**
	 * Function for rendering custom option block.
	 */
	optionRenderer?: (option: Option<T>, key: number, onClick: (event: React.MouseEvent) => void) => React.ReactNode;

	/**
	 * Function for rendering custom dropdown block.
	 */
	dropdownRenderer?: (parameters: DropdownRendererParameters<T>) => React.ReactNode;

	/**
	 * Input value handler.
	 */
	onInputChange?: (event: React.ChangeEvent<HTMLInputElement>, value?: any) => void;

	/**
	 * Option value select handler.
	 */
	onSelect?: (value: T) => void;

	callOnSelectOn?: 'click' | 'mouseDown'; // https://github.com/mui/material-ui/issues/19038

	/**
	 * Props for controlling onClickOutside HOC.
	 */
	onClickOutsideProps?: AdditionalProps;
	label?: string;
	endAdornment?: React.ReactNode;
	error?: string;
	name?: string;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onFocus?: React.FocusEventHandler;

	innerRef?: React.Ref<HTMLDivElement>;

	disabled?: boolean;

	variant?: 'standard' | 'filled' | 'outlined';
	autofocus?: boolean;
}

export type AutocompleteProps<T> = OwnProps<T>;

const Autocomplete = <T extends unknown>(props: AutocompleteProps<T>): JSX.Element => {
	const {
		className,
		dropDownClassName,
		isLoading,
		options,
		inputValue,
		inputRenderer,
		groupRenderer,
		optionRenderer,
		dropdownRenderer,
		onInputChange,
		onSelect,
		callOnSelectOn,
		onClickOutsideProps,
		label,
		endAdornment,
		error,
		name,
		onBlur,
		onFocus,
		innerRef,
		disabled,
		variant,
		autofocus,
		...restProps
	} = props;

	const { Autocomplete: css } = useTheme('Autocomplete');

	const [isOpen, setIsOpen] = useState(false);

	const handleFocus = useCallback(
		(event: React.FocusEvent): void => {
			onFocus?.(event);
			setIsOpen(true);
		},
		[onFocus]
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent<HTMLInputElement>): void => {
			setTimeout(() => {
				setIsOpen(false);
			}, 200);

			onBlur?.(event);
		},
		[onBlur]
	);

	const handleClose = useCallback((): void => {
		setIsOpen(false);
	}, []);

	const getOnSelect = useCallback(
		(item: T) => () => {
			onSelect(item);
			handleClose();
		},
		[onSelect, handleClose]
	);

	const renderOption = useCallback(
		(option: Option<T>, key: number): React.ReactNode => {
			// Custom renderer.
			if (optionRenderer) {
				return optionRenderer(option, key, getOnSelect(option.value));
			}

			if (callOnSelectOn === 'mouseDown') {
				return (
					<MenuItem key={key} className={css.option} onMouseDown={getOnSelect(option.value)}>
						{option.label}
					</MenuItem>
				);
			}

			return (
				<MenuItem key={key} className={css.option} onClick={getOnSelect(option.value)}>
					{option.label}
				</MenuItem>
			);
		},
		[optionRenderer, callOnSelectOn, getOnSelect]
	);

	const renderGroup = useCallback(
		(group: Group<T>, key: number): React.ReactNode => {
			// Custom renderer.
			if (groupRenderer) {
				return groupRenderer(group, key, renderOption);
			}

			return (
				!!group.options.length && (
					<div key={key} className={css.group}>
						<div className={css.group__label}>{group.label}</div>
						<div className={css.group__options}>{group.options.map(renderOption)}</div>
					</div>
				)
			);
		},
		[groupRenderer, renderOption]
	);

	const renderInput = useMemo((): React.ReactNode => {
		const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
			type: 'text',
			className: css.input,
			value: inputValue,
			onChange: onInputChange,
			onFocus: handleFocus,
			onBlur: handleBlur,
			spellCheck: false,
			autoComplete: 'off',
			autoCorrect: 'off'
		};

		// Custom renderer.
		if (inputRenderer) {
			return inputRenderer(isOpen, isLoading, inputProps);
		}

		return (
			<Input
				label={label}
				value={inputValue}
				name={name || ''}
				className={css.input}
				autoFocus={autofocus}
				disabled={disabled}
				variant={variant}
				TextFieldProps={{
					error: !!error,
					helperText: error,
					onBlur: handleBlur,
					onFocus: handleFocus,
					autoComplete: inputProps.autoComplete,
					InputProps: {
						onChange: onInputChange,
						endAdornment: endAdornment || '',
						autoComplete: inputProps.autoComplete,
						className: css.inner_input
					}
				}}
				absoluteHintPosition={true}
			/>
		);
	}, [
		inputValue,
		onInputChange,
		handleFocus,
		handleBlur,
		inputRenderer,
		isOpen,
		isLoading,
		label,
		name,
		autofocus,
		disabled,
		variant,
		error,
		endAdornment
	]);

	const renderDropdown = useMemo((): React.ReactNode => {
		// Custom renderer.
		if (dropdownRenderer) {
			return dropdownRenderer({
				options,
				isOpen,
				onClose: handleClose,
				onInputChange,
				onSelect,
				renderGroup,
				renderOption
			});
		}

		return (
			<Dropdown {...onClickOutsideProps} isOpen={options.length > 0 && isOpen} onClose={handleClose}>
				<Paper className={cn(css.options, dropDownClassName)} data-test-id="options">
					{options.map((item, index) =>
						'value' in item ? renderOption(item, index) : renderGroup(item, index)
					)}
				</Paper>
			</Dropdown>
		);
	}, [
		dropdownRenderer,
		options,
		isOpen,
		handleClose,
		onInputChange,
		onSelect,
		renderGroup,
		renderOption,
		onClickOutsideProps,
		dropDownClassName
	]);

	return (
		<div ref={innerRef} className={cn(css.autocomplete, className)} {...restProps}>
			{renderInput}
			{renderDropdown}
		</div>
	);
};

Autocomplete.displayName = 'Autocomplete';

export default Autocomplete;
